// Catalog.ID Client — Per-key evidence of use (device-setup-restructure §9.4)
// ============================================================================
// The Devices pane's "Keys on this device" group shows, per optional key, what
// this device last did with it. That evidence is recorded HERE, by the CID tab
// itself, at the moment an operation succeeds — never by the server, which by
// design learns neither which keys a device holds (§7.1) nor what it does with
// them.
//
// Two wordings, and the distinction is the point (§1 item 15):
//
//   `kind: 'oracle'`  CID performed the operation and handed back only the
//                     RESULT — a cert, a decapsulated epoch key, a token. The
//                     private key never left this tab, so the record is exact:
//                     which module asked, which mode, when.
//   `kind: 'copy'`    the key's BYTES left the tab (`export-mail-encrypt`, and
//                     `release` when M6b instruments it). CID cannot know what
//                     the module did afterwards, so the record is the
//                     HAND-OVER, and the pane words it as such. Removing the
//                     key from this device does not take that copy back.
//
// ── what is NOT recorded, and why ───────────────────────────────────────────
//  * The three mandatory keys (`main_signing_key`, `attr_encrypt`,
//    `derive_root`). There is nothing to decide about them — they are on every
//    device that stores keys at all — and `derive_root` in particular would
//    otherwise be written on every derive, every blob read and every token
//    mint. Enforced below, not left to each call site.
//  * Plain reads of `state/keys.ts`. A read is not a use: `getPrivateKey` is
//    called by panes that render, and instrumenting it would make "last used"
//    mean "last looked at" (§9.4, §2 item 9).
//
// Swept with the device copy. The sweep in `clearDeviceStorage` lands with
// M6b — that file belongs to another milestone this wave — so
// {@link clearKeyUseRecord} is exported ready for it.

import {
    AGENT_SIGNING_KEY_FAMILY, MANDATORY_DEVICE_KEY_IDS, catalogueRowForKeyId,
} from './device-key-selection';

/** How the key was used: CID performed the operation (`oracle`), or the key's
 *  bytes were handed to the requester (`copy`). */
export type KeyUseKind = 'oracle' | 'copy';

/** The last thing this device did with one key. */
export interface KeyUseEvent {
    /** ISO-8601 UTC, stamped by the tab that performed the operation. */
    at: string;
    /** The module that asked — `efs`, `chat`, `mail`, `xrpl`, … */
    requesterId: string;
    /** The request mode, verbatim (`issue-cert`, `export-mail-encrypt`, …). */
    mode: string;
    kind: KeyUseKind;
}

/** What one call hands in. `at` defaults to now. */
export type KeyUseInput = Omit<KeyUseEvent, 'at'> & { at?: string };

/** The stored shape: `{ v: 1, [keyId]: KeyUseEvent }` — one event per key id,
 *  the LAST one. Not a history: the pane shows one line per key, the record is
 *  swept with the device, and a log of every cross-module operation is exactly
 *  the kind of local surveillance trail §3.4.7 exists to avoid. */
export interface StoredKeyUseRecord {
    v: 1;
    [keyId: string]: KeyUseEvent | 1;
}

/** `{cid}_keyuse` — beside the manifest, and deliberately OUTSIDE the
 *  `{cid}_key_` namespace: `hasStoredKeys` is a bare prefix scan over that
 *  namespace, so a record filed under it would make a browser holding evidence
 *  but no keys look enrolled (M6b finding, 2026-09-17). `clearDeviceStorage`
 *  sweeps this key explicitly through `clearKeyUseRecord`. */
export function keyUseRecordStorageKey(cid: string): string {
    return `${cid}_keyuse`;
}

/**
 * The id this key is RECORDED under. Agent keys are minted per agent
 * (`agent:{cid}:agent_signing_key`) and the picker offers them as one family
 * toggle ({@link AGENT_SIGNING_KEY_FAMILY}), so their evidence belongs on that
 * one row too — otherwise the pane would grow a line per agent and the row the
 * member can actually untick would show nothing.
 */
export function keyUseRecordIdFor(keyId: string): string {
    return catalogueRowForKeyId(keyId)?.family ?? keyId;
}

function storage(): Storage | null {
    try {
        return globalThis.localStorage;
    } catch {
        return null; // privacy modes and non-DOM environments both throw here
    }
}

/**
 * Note that this device used `keyId`. Called by the dispatchers at the point
 * the operation SUCCEEDS, never before it: a refused, declined or failed
 * request is not a use, and recording one would put a line in the pane the
 * member cannot explain.
 *
 * Total and silent. A mandatory key id writes nothing (see the header), and
 * every storage failure — quota, privacy mode, a shim that throws — is
 * swallowed: the operation it decorates has already succeeded, and losing a
 * display record must never turn that into a failure.
 */
export function recordKeyUse(cid: string, keyId: string, use: KeyUseInput): void {
    if (MANDATORY_DEVICE_KEY_IDS.includes(keyId)) return;
    const s = storage();
    if (!s) return;

    const event: KeyUseEvent = {
        at: use.at ?? new Date().toISOString(),
        requesterId: use.requesterId,
        mode: use.mode,
        kind: use.kind,
    };

    try {
        const next: StoredKeyUseRecord = { v: 1, ...readKeyUseRecord(cid) };
        next[keyUseRecordIdFor(keyId)] = event;
        s.setItem(keyUseRecordStorageKey(cid), JSON.stringify(next));
    } catch {
        // See the doc comment: a display record never fails an operation.
    }
}

/**
 * This device's events for an account, key id → last event. Absent, malformed
 * or future-versioned storage all read as `{}` — "not used on this device",
 * which is what the pane shows and is the only honest answer when the record
 * cannot be trusted.
 */
export function readKeyUseRecord(cid: string): Record<string, KeyUseEvent> {
    const s = storage();
    if (!s) return {};

    let raw: string | null;
    try {
        raw = s.getItem(keyUseRecordStorageKey(cid));
    } catch {
        return {};
    }
    if (raw === null) return {};

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return {};
    }
    if (typeof parsed !== 'object' || parsed === null) return {};
    const record = parsed as Partial<StoredKeyUseRecord>;
    if (record.v !== 1) return {};

    const out: Record<string, KeyUseEvent> = {};
    for (const [keyId, value] of Object.entries(record)) {
        if (keyId === 'v') continue;
        if (!isKeyUseEvent(value)) continue;
        out[keyId] = value;
    }
    return out;
}

function isKeyUseEvent(value: unknown): value is KeyUseEvent {
    if (typeof value !== 'object' || value === null) return false;
    const e = value as Partial<KeyUseEvent>;
    return typeof e.at === 'string' && typeof e.requesterId === 'string'
        && typeof e.mode === 'string' && (e.kind === 'oracle' || e.kind === 'copy');
}

/** Forget one identity's record. Swept with the device copy by
 *  `clearDeviceStorage` (wired in M6b — see the module header). */
export function clearKeyUseRecord(cid: string): void {
    try {
        storage()?.removeItem(keyUseRecordStorageKey(cid));
    } catch {
        // Same posture as the write: never throw out of a sweep.
    }
}
